import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Landmark, TrendingUp, Wrench, Send, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TICKET_META = [
  { key: 'aperto', label: 'Aperti', color: 'bg-red-500', text: 'text-red-600' },
  { key: 'in_lavorazione', label: 'In lavorazione', color: 'bg-amber-500', text: 'text-amber-600' },
  { key: 'chiuso', label: 'Chiusi', color: 'bg-emerald-500', text: 'text-emerald-600' },
];

function WidgetSdi({ sdi }) {
  return (
    <Card className="flex flex-col" data-testid="widget-sdi">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-mono uppercase flex items-center gap-2">
          <Landmark className="h-4 w-4 text-primary" /> Stato Fatture SdI
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 space-y-3">
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-md border border-sky-500/40 bg-sky-50 dark:bg-sky-950/30 p-2 text-center">
            <Send className="h-4 w-4 mx-auto text-sky-600" />
            <p className="text-xl font-bold text-sky-600" data-testid="sdi-inviate">{sdi.inviate}</p>
            <p className="text-[10px] uppercase text-muted-foreground">Inviate</p>
          </div>
          <div className="rounded-md border border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/30 p-2 text-center">
            <CheckCircle2 className="h-4 w-4 mx-auto text-emerald-600" />
            <p className="text-xl font-bold text-emerald-600" data-testid="sdi-consegnate">{sdi.consegnate}</p>
            <p className="text-[10px] uppercase text-muted-foreground">Consegnate</p>
          </div>
          <div className="rounded-md border border-red-500/40 bg-red-50 dark:bg-red-950/30 p-2 text-center">
            <XCircle className="h-4 w-4 mx-auto text-red-600" />
            <p className="text-xl font-bold text-red-600" data-testid="sdi-scartate">{sdi.scartate}</p>
            <p className="text-[10px] uppercase text-muted-foreground">Scartate</p>
          </div>
        </div>
        {sdi.errori.length > 0 ? (
          <div className="space-y-1.5">
            <p className="text-[10px] uppercase font-mono text-muted-foreground">Scartate — motivo</p>
            {sdi.errori.slice(0, 4).map((e) => (
              <div key={`${e.numero}-${e.cliente}`} className="rounded-md border-l-2 border-red-500 bg-red-50/60 dark:bg-red-950/20 px-2 py-1.5">
                <p className="text-xs font-semibold">{e.numero} · {e.cliente}</p>
                <p className="text-[11px] text-red-600">{e.motivo}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            {sdi.inviate + sdi.consegnate === 0
              ? 'Nessuna fattura inviata a SdI finora. Usa il check verde in Documenti dopo il caricamento su Aruba.'
              : 'Nessuna fattura scartata ✓'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function WidgetVendite({ vendite }) {
  const mese = new Date().toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  return (
    <Card className="flex flex-col" data-testid="widget-vendite">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-mono uppercase flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" /> Analisi Vendite
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1">
        <div className="flex items-baseline gap-2 mb-2">
          <span className="text-2xl font-bold" data-testid="vendite-totale">
            € {vendite.totale.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
          </span>
          <span className="text-xs text-muted-foreground capitalize">fatturato — {mese}</span>
        </div>
        {vendite.giorni.length > 0 ? (
          <div className="h-[150px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={vendite.giorni} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.1} />
                <XAxis dataKey="g" tick={{ fontSize: 10 }} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                <Tooltip formatter={(v) => [`€ ${Number(v).toLocaleString('it-IT')}`, 'Fatturato']} labelFormatter={(g) => `Giorno ${g}`} />
                <Bar dataKey="tot" fill="hsl(var(--primary))" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground py-8 text-center">Nessuna fattura emessa questo mese</p>
        )}
      </CardContent>
    </Card>
  );
}

function WidgetOperativo({ ticket }) {
  const totale = TICKET_META.reduce((s, t) => s + (ticket[t.key] || 0), 0);
  return (
    <Card className="flex flex-col" data-testid="widget-operativo">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-mono uppercase flex items-center gap-2">
          <Wrench className="h-4 w-4 text-primary" /> Gestione Operativa
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-1 space-y-3">
        <p className="text-2xl font-bold" data-testid="ticket-totale">
          {totale} <span className="text-xs font-normal text-muted-foreground">ticket / interventi totali</span>
        </p>
        {totale > 0 && (
          <div className="flex h-2.5 rounded-full overflow-hidden">
            {TICKET_META.map((t) => (
              <div key={t.key} className={t.color} style={{ width: `${((ticket[t.key] || 0) / totale) * 100}%` }} title={`${t.label}: ${ticket[t.key] || 0}`} />
            ))}
          </div>
        )}
        <div className="space-y-2">
          {TICKET_META.map((t) => (
            <div key={t.key} className="flex items-center justify-between rounded-md border border-border px-3 py-2">
              <span className="flex items-center gap-2 text-sm">
                <span className={`w-2.5 h-2.5 rounded-full ${t.color}`} /> {t.label}
              </span>
              <span className={`font-bold ${t.text}`} data-testid={`ticket-${t.key}`}>{ticket[t.key] || 0}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export const WidgetsSdiVenditeOperativo = () => {
  const [dati, setDati] = useState(null);
  useEffect(() => {
    axios.get(`${API}/dashboard/widgets`).then((r) => setDati(r.data)).catch(() => setDati(null));
  }, []);

  if (!dati) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="widgets-sdi-vendite-operativo">
        {[1, 2, 3].map((i) => (
          <Card key={i}><CardContent className="py-12 text-center text-muted-foreground text-sm"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Caricamento…</CardContent></Card>
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="widgets-sdi-vendite-operativo">
      <WidgetSdi sdi={dati.sdi} />
      <WidgetVendite vendite={dati.vendite} />
      <WidgetOperativo ticket={dati.ticket} />
    </div>
  );
};
